/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentType } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { LoggedUser } from 'entities';

import { AppWrapper } from 'components/AppWrapper';

import { useUser } from 'hooks/user';

import { cascadeEval } from 'utils/conditions';

import { PageTitle } from './Title';
import { RoutePermissions, shouldBlockRoute } from '../config';

interface BaseProps {
  isPrivate?: boolean;
  component?: ComponentType;
  redirect?: string;
  permanent?: boolean;
  pageKey?: string;

  title?: string;
  customPermissionCheck?: (user: LoggedUser) => boolean;
  permissions?: RoutePermissions;
}

type RouteOptions =
  | { component: ComponentType; redirect?: never }
  | { component?: never; redirect: string };

export type CustomRouteProps = BaseProps & RouteOptions;

const REDIRECT_PATH_PARAM = '__redirect';

export function CustomRoute({
  isPrivate = false,
  component: Component,
  redirect,
  pageKey,
  permanent,
  customPermissionCheck,
  permissions,
  title,
}: CustomRouteProps) {
  const user = useUser();
  const { pathname } = useLocation();
  const [search] = useSearchParams();

  const isSigned = !!user?.email;

  const RenderComponent = Component as any;

  if (redirect) return <Navigate to={redirect} replace />;

  if (!isPrivate && permanent) return <RenderComponent key={pageKey} />;

  const noPermission = cascadeEval([
    { is: !isSigned, then: false },
    {
      is: !!permissions || customPermissionCheck,
      then: () => shouldBlockRoute({ permissions, customPermissionCheck }, user),
    },
    { is: true, then: false },
  ]);

  if (noPermission) return <Navigate to="/" replace />;

  const redirectAfter = isPrivate && pathname !== '/';

  const loggedPath = decodeURIComponent(search.get(REDIRECT_PATH_PARAM) ?? '/');

  return isPrivate === isSigned ? (
    RenderComponent && (
      <AppWrapper>
        <PageTitle title={title}>
          <RenderComponent key={pageKey} />
        </PageTitle>
      </AppWrapper>
    )
  ) : (
    <Navigate
      replace
      to={{
        pathname: isPrivate ? '/login' : loggedPath,

        search: redirectAfter
          ? `?${REDIRECT_PATH_PARAM}=${encodeURIComponent(pathname)}`
          : undefined,
      }}
    />
  );
}
